import React from "react";
import { Alert, Box, Grid, Paper, Typography } from "@mui/material";
import {
  BooleanInput,
  DateField,
  Edit,
  BooleanField,
  FileField,
  FileInput,
  FormDataConsumer,
  Labeled,
  SimpleForm,
  TextField,
  TextInput,
  useGetList,
} from "react-admin";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { RichTextInput, RichTextInputToolbar } from "ra-input-rich-text";
import EditTitle from "../../../ui/edit-title";
import CustomEditToolbar from "../../../ui/custom-edit-toolbar";
import CustomTextField from "../../../ui/custom-text-field";
import CustomSelect from "../../../ui/custom-select";
import { hasValue, minLength } from "../../../utils/schemas";
import { ContentPageSX } from "../../../styled/custom";
import MediaUploadProgressOverlay from "../../../ui/media-upload-progress-overlay";
import ThumbnailUploadInput from "../../../ui/thumbnail-upload-input";
import { MEDIA_ACCEPT_BY_CLASS, MEDIA_MAXIMUM_FILES_SIZE } from "./common/constants";
import {
  getEligibleTypePlanIds,
  normalizeIds,
  renderTypePlanDialogExtra,
  renderTypePlanItem,
  withPlanAvailability,
} from "./common/type-plan-utils";

const sectionSx = {
  p: 2,
  borderRadius: "10px",
  border: "1px solid rgba(0,0,0,0.09)",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  height: "100%",
};

const sectionTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "#2f2f2f",
  mb: 1.2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const Schema = Yup.object({
  category_media_id: hasValue("Selecione uma categoria"),
  name: Yup.string().when("media_class", {
    is: "Texto",
    then: Yup.string().required("O titulo e obrigatorio para a classe Texto"),
    otherwise: minLength(3, "Digite o nome"),
  }),
  body: Yup.string().trim().when("media_class", {
    is: "Texto",
    then: Yup.string().trim().nullable().required("O corpo e obrigatorio para a classe Texto"),
    otherwise: Yup.string().nullable().notRequired(),
  }),
  thumbnail_file: Yup.mixed().notRequired(),
  expires_at: Yup.string()
    .nullable()
    .test("valid-date", "Data de expiração inválida", (value) => {
      if (!value) return true;
      return !Number.isNaN(new Date(value).getTime());
    })
    .test("future-plus-1-day", "A data de expiração deve ser no mínimo 1 dia no futuro", (value) => {
      if (!value) return true;
      const selected = new Date(value);
      const minDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
      return selected.getTime() >= minDate.getTime();
    }),
});

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const MediaEdit = () => {
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetList("category-media", {
    pagination: { page: 1, perPage: 1000000 },
  });
  const { data: typePlansData, isLoading: isLoadingTypePlans } = useGetList("type-plan", {
    pagination: { page: 1, perPage: 1000000 },
    filter: { activated: true },
  });

  const transformMediaPayload = (formData) => {
    const eligibleTypePlanIds = getEligibleTypePlanIds(
      typePlansData || [],
      formData.category_media_id,
      formData.media_class
    );
    const eligibleSet = new Set(eligibleTypePlanIds);

    return {
      ...formData,
      type_plan_ids: JSON.stringify(
        normalizeIds(formData.type_plan_ids).filter((typePlanId) => eligibleSet.has(typePlanId))
      ),
    };
  };

  return (
    <Edit
      title={<EditTitle subtitle="Editar Produto da Cesta: " source="name" />}
      redirect="list"
      mutationMode="pessimistic"
      sx={ContentPageSX}
      transform={transformMediaPayload}
    >
      <>
        <MediaUploadProgressOverlay />
        <SimpleForm
          noValidate
          resolver={yupResolver(Schema)}
          toolbar={<CustomEditToolbar />}
          // sx={{ maxWidth: "1180px", mx: "auto", width: "100%" }}
        >
          <FormDataConsumer>
            {({ formData }) => {
              const isHypermedia = formData.media_class !== "Texto";
              const fileAccept =
                MEDIA_ACCEPT_BY_CLASS[formData.media_class] ?? MEDIA_ACCEPT_BY_CLASS.Audio;
              const plansWithAvailability = withPlanAvailability(
                typePlansData || [],
                formData.media_class,
                formData.category_media_id
              );
              const selectedTypePlanIds = normalizeIds(formData.type_plan_ids);
              const unsupportedSelectedPlans = plansWithAvailability.filter(
                (plan) => plan.disabled && selectedTypePlanIds.includes(Number(plan.id))
              );

              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Paper sx={sectionSx}>
                    <Typography sx={sectionTitleSx}>Dados Gerais</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={2}>
                        <Labeled label="Id">
                          <TextField source="id" />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Labeled label="Classe">
                          <TextField source="media_class" />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Labeled label="Criado em">
                          <DateField source="created_at" locales="pt-BR" showTime />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextInput
                          source="expires_at"
                          label="Expira em"
                          fullWidth
                          type="datetime-local"
                          format={toDateTimeLocalValue}
                          parse={(value) => (value ? value : null)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Labeled label="Expirado">
                          <BooleanField source="is_expired" />
                        </Labeled>
                      </Grid>
                      <Grid item xs={12} md={12}>
                        {!isLoadingCategories && (
                          <CustomSelect
                            data={categoriesData}
                            name="category_media_id"
                            label="Categoria"
                            multiple={false}
                            fullWidth
                            isRequired
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={12}>
                        {!isLoadingTypePlans && (
                          <CustomSelect
                            data={plansWithAvailability}
                            name="type_plan_ids"
                            label="Planos Disponíveis"
                            multiple={true}
                            fullWidth
                            optionDisabled="disabled"
                            renderItem={renderTypePlanItem}
                            renderDialogExtra={renderTypePlanDialogExtra}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {unsupportedSelectedPlans.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Alguns planos selecionados nao tem mais suporte para esta midia e serao removidos ao salvar.
                      </Alert>
                    )}
                  </Paper>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Paper sx={sectionSx}>
                        <Typography sx={sectionTitleSx}>Conteúdo</Typography>
                        <CustomTextField name="name" label={isHypermedia ? "Nome" : "Titulo"} fullWidth />
                        {!isHypermedia && (
                          <RichTextInput
                            source="body"
                            fullWidth
                            label="Corpo"
                            toolbar={<RichTextInputToolbar size="medium" />}
                          />
                        )}
                        {isHypermedia && <CustomTextField name="description" label="Descricao" fullWidth />}
                        {isHypermedia && (
                          <FileInput
                            source="file"
                            label="Tamanho Maximo do Arquivo: 2GB"
                            maxSize={MEDIA_MAXIMUM_FILES_SIZE}
                            accept={fileAccept}
                          >
                            <FileField src="src" title="title" />
                          </FileInput>
                        )}
                        <BooleanInput source="active" label="Ativo" />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper sx={sectionSx}>
                        <Typography sx={sectionTitleSx}>Thumbnail</Typography>
                        <ThumbnailUploadInput
                          source="thumbnail_file"
                          nameValue={formData.name}
                          thumbnailUrlValue={formData.thumbnail_url}
                        />
                        {formData.media_class === "Video" && (
                          <BooleanInput
                            source="thumbnail_from_video_frame"
                            label="Gerar thumbnail do primeiro frame (quando nenhuma imagem for enviada)"
                          />
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              );
            }}
          </FormDataConsumer>
        </SimpleForm>
      </>
    </Edit>
  );
};

export default MediaEdit;
